import { QuarterData, TickerAnalystView, TickerPast4QuartersAnalystViewsData } from '../types/analystViews';
import { ANALYST_VIEWS_STATISTICS } from './analystViewsTabUtils';

/**
 * Format quarter header as QQ YYYY or FY YYYY
 */
export const formatQuarterHeader = (quarter: QuarterData): string => {
  if (quarter.quarter === null) {
    return `FY ${quarter.year}`;
  }
  return `Q${quarter.quarter} ${quarter.year}`;
};

/**
 * Format date to MM/DD/YYYY
 */
export const formatAnalystViewsDate = (dateString: string): string => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString();
};

/**
 * Transform analyst views data for display
 * Converts raw analyst views array into a structured object with all statistics
 */
export const transformAnalystViewsData = (analystViews: TickerAnalystView[]): Record<string, any> => {
  const result: Record<string, any> = {};

  // Group by category and key
  const viewsByCategory = analystViews.reduce((acc: any, view: any) => {
    if (!acc[view.category]) {
      acc[view.category] = {};
    }
    acc[view.category][view.key] = view.value;
    return acc;
  }, {} as Record<string, Record<string, number>>);

  // Price Targets
  const priceTargets = viewsByCategory.price_targets || {};
  if (priceTargets.targetMeanPrice) {
    result.averagePriceTarget = priceTargets.targetMeanPrice;
  }
  if (priceTargets.targetHighPrice && priceTargets.targetLowPrice) {
    result.priceTargetRange = `$${priceTargets.targetLowPrice.toFixed(2)} - $${priceTargets.targetHighPrice.toFixed(2)}`;
  }

  // Recommendations
  const recommendations = viewsByCategory.recommendations || {};
  const totalRecommendations = (recommendations.strongBuy || 0) + (recommendations.buy || 0) + 
                             (recommendations.hold || 0) + (recommendations.sell || 0) + 
                             (recommendations.strongSell || 0);
  
  if (totalRecommendations > 0) {
    result.strongBuyCount = recommendations.strongBuy || 0;
    result.buyCount = recommendations.buy || 0;
    result.holdCount = recommendations.hold || 0;
    result.sellCount = recommendations.sell || 0;
    result.strongSellCount = recommendations.strongSell || 0;
    
    // Calculate percentages
    result.strongBuyPercent = ((recommendations.strongBuy || 0) / totalRecommendations) * 100;
    result.buyPercent = ((recommendations.buy || 0) / totalRecommendations) * 100;
    result.holdPercent = ((recommendations.hold || 0) / totalRecommendations) * 100;
    result.sellPercent = ((recommendations.sell || 0) / totalRecommendations) * 100;
    result.strongSellPercent = ((recommendations.strongSell || 0) / totalRecommendations) * 100;
    
    // Calculate average recommendation
    const avgScore = ((recommendations.strongBuy || 0) * 5 + (recommendations.buy || 0) * 4 + 
                    (recommendations.hold || 0) * 3 + (recommendations.sell || 0) * 2 + 
                    (recommendations.strongSell || 0) * 1) / totalRecommendations;
    
    let recommendationText = '';
    if (avgScore >= 4.5) recommendationText = 'Strong Buy';
    else if (avgScore >= 3.5) recommendationText = 'Buy';
    else if (avgScore >= 2.5) recommendationText = 'Hold';
    else if (avgScore >= 1.5) recommendationText = 'Sell';
    else recommendationText = 'Strong Sell';
    
    result.averageRecommendation = `${recommendationText} (${avgScore.toFixed(1)})`;
  }

  // Earnings
  const earnings = viewsByCategory.earnings || {};
  
  // This Quarter (0q)
  if (earnings['0q_eps_avg']) {
    result.thisQuarterEpsAvg = earnings['0q_eps_avg'];
    if (earnings['0q_eps_high'] && earnings['0q_eps_low']) {
      result.thisQuarterEpsRange = `$${earnings['0q_eps_low'].toFixed(2)} - $${earnings['0q_eps_high'].toFixed(2)}`;
    }
  }
  
  // Next Quarter (+1q)
  if (earnings['+1q_eps_avg']) {
    result.nextQuarterEpsAvg = earnings['+1q_eps_avg'];
    if (earnings['+1q_eps_high'] && earnings['+1q_eps_low']) {
      result.nextQuarterEpsRange = `$${earnings['+1q_eps_low'].toFixed(2)} - $${earnings['+1q_eps_high'].toFixed(2)}`;
    }
  }
  
  // This Year (0y)
  if (earnings['0y_eps_avg']) {
    result.thisYearEpsAvg = earnings['0y_eps_avg'];
    if (earnings['0y_eps_high'] && earnings['0y_eps_low']) {
      result.thisYearEpsRange = `$${earnings['0y_eps_low'].toFixed(2)} - $${earnings['0y_eps_high'].toFixed(2)}`;
    }
  }
  
  // Next Year (+1y)
  if (earnings['+1y_eps_avg']) {
    result.nextYearEpsAvg = earnings['+1y_eps_avg'];
    if (earnings['+1y_eps_high'] && earnings['+1y_eps_low']) {
      result.nextYearEpsRange = `$${earnings['+1y_eps_low'].toFixed(2)} - $${earnings['+1y_eps_high'].toFixed(2)}`;
    }
  }

  return result;
};

/**
 * Format value for display in analyst views table
 */
export const formatAnalystViewsValue = (value: any, statisticKey: string): string => {
  if (value === undefined || value === null || value === '-') return '-';
  
  // Handle date formatting for nextEarningsDate
  if (statisticKey === 'nextEarningsDate') {
    let earningsDate: Date | null = null;
    
    if (value instanceof Date) {
      earningsDate = value;
    } else if (typeof value === 'string') {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        earningsDate = date;
      }
    }
    
    if (earningsDate) {
      // Get today's date in New York time
      const now = new Date();
      const nyTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
      const todayNY = new Date(nyTime.getFullYear(), nyTime.getMonth(), nyTime.getDate());
      
      // Get the earnings date at midnight in New York time for comparison
      const earningsDateNY = new Date(earningsDate.toLocaleString("en-US", { timeZone: "America/New_York" }));
      const earningsDateOnly = new Date(earningsDateNY.getFullYear(), earningsDateNY.getMonth(), earningsDateNY.getDate());
      
      // If earnings date is before today in NY time, show "TBD"
      if (earningsDateOnly < todayNY) {
        return 'TBD';
      }
      
      return earningsDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    
    return '-';
  }
  
  if (typeof value === 'number') {
    if (statisticKey.includes('Percent') || statisticKey === 'priceTargetDifference') {
      return `${value.toFixed(1)}%`;
    }
    if (statisticKey.includes('Price') || statisticKey.includes('Eps') || statisticKey === 'lastClosingPrice') {
      return `$${value.toFixed(2)}`;
    }
    return value.toString();
  }
  
  return value.toString();
};

/**
 * Get statistic color for analyst views
 */
export const getAnalystViewsStatisticColor = (statisticKey: string, value: any): string => {
  // Color coding for average recommendation
  if (statisticKey === 'averageRecommendation' && typeof value === 'string') {
    if (value.includes('Strong Buy') || value.includes('Buy')) {
      return 'text-green-light dark:text-green-500';
    } else if (value.includes('Strong Sell') || value.includes('Sell')) {
      return 'text-red-light dark:text-red-500';
    } else if (value.includes('Hold')) {
      return 'text-text-light-neutral dark:text-text-secondary';
    }
  }
  
  // Color coding for price target difference
  if (statisticKey === 'priceTargetDifference' && typeof value === 'number') {
    if (value > 0) {
      return 'text-green-light dark:text-green-500';
    } else if (value < 0) {
      return 'text-red-light dark:text-red-500';
    }
  }
  
  return 'text-gray-700 dark:text-text-secondary';
};

/**
 * Get quarter data for a specific quarter
 * @param quarter - The quarter data
 * @param quarterIndex - Index of the quarter (0 for latest)
 * @param analystViewsData - The full analyst views data
 * @param priceData - Optional price data for latest quarter calculations
 * @param nextEarningsDate - Optional next earnings date (only shown for latest quarter)
 */
export const getAnalystViewsQuarterData = (
  quarter: QuarterData,
  quarterIndex: number,
  analystViewsData: TickerPast4QuartersAnalystViewsData | null,
  priceData?: any,
  nextEarningsDate?: Date | null
): Record<string, any> => {
  if (!analystViewsData) return {};
  
  // Filter analyst views for this specific quarter
  const quarterViews = analystViewsData.analystViews.filter((view: TickerAnalystView) => 
    view.year === quarter.year && view.quarter === quarter.quarter
  );
  
  const transformedData = transformAnalystViewsData(quarterViews);
  
  // For non-latest quarters, set specific statistics to "-"
  if (quarterIndex > 0) {
    const statisticsToHide = [
      'lastClosingPrice',
      'priceTargetDifference',
      'thisQuarterEpsAvg',
      'thisQuarterEpsRange',
      'nextQuarterEpsAvg',
      'nextQuarterEpsRange',
      'nextEarningsDate'
    ];
    
    statisticsToHide.forEach(stat => {
      transformedData[stat] = '-';
    });
  } else {
    // Only for the latest quarter (index 0), add calculated values
    if (priceData) {
      // Last Closing Price - use the current price from price data
      transformedData.lastClosingPrice = priceData.currentPrice || priceData.close_price_adjusted;
      
      // Potential Upside - calculate based on average price target and current price
      const averagePriceTarget = transformedData.averagePriceTarget;
      if (averagePriceTarget && priceData.currentPrice) {
        transformedData.priceTargetDifference = ((averagePriceTarget - priceData.currentPrice) / priceData.currentPrice) * 100;
      }
    }
    
    // Add next earnings date only for the latest quarter
    if (nextEarningsDate) {
      transformedData.nextEarningsDate = nextEarningsDate;
    }
  }
  
  return transformedData;
};

/**
 * Calculate statistics column width based on longest indicator name
 * @param isEditingColumns - Whether edit mode is enabled (adds checkbox width)
 */
export const calculateAnalystViewsStatisticsColumnWidth = (isEditingColumns: boolean = false): number => {
  // Find the longest line across all labels (handles multi-line labels with \n)
  const longestLine = ANALYST_VIEWS_STATISTICS.reduce((longest, stat) => {
    const lines = stat.label.split('\n');
    const longestInStat = lines.reduce((max, line) => 
      line.length > max.length ? line : max, ''
    );
    return longestInStat.length > longest.length ? longestInStat : longest;
  }, '');
  
  // Base width calculation: character count * 8px + padding + buffer
  const baseWidth = longestLine.length * 8; // ~8px per character
  const padding = 32; // 16px on each side (px-4)
  const buffer = 20; // Extra buffer to prevent cutoff
  
  // Add extra width for checkbox when in edit mode
  const checkboxWidth = isEditingColumns ? 40 : 0; // 40px for checkbox + gap
  
  return Math.max(300, baseWidth + padding + buffer + checkboxWidth); // Minimum 300px
};

